<template>
  <md-card>
    <slot name="header">
      <md-card-header v-if="header" class="text-center">
        <h4>{{ header }}</h4>
      </md-card-header>
    </slot>
    <KDraggableField
      class="file-card"
      :disabled="!!toUploadPhotos.length || loading || !showUploadButton"
      :exe="() => ($refs.file && $refs.file.click())"
      @input="onFileChange($event)"
    >
      <md-card-content style="width: 100%; padding: 0;" v-if="toUploadPhotos.length && !loading">
        <div class="md-layout md-gutter md-alignment-center-center">
          <div class="md-layout-item md-size-60">
            <slot name="top-select"></slot>

            <md-field>
              <label>{{ conceptLabel }}</label>
              <md-select v-model="selectedConceptId">
                <md-option v-for="concept in concepts" :key="concept.id" :value="concept.id">
                  <slot name="concept-name" :concept="concept">{{ concept.name }}</slot>
                </md-option>
              </md-select>
            </md-field>

            <slot name="bottom-select"></slot>
          </div>

          <div class="md-layout-item md-size-20 md-group">
            <md-button
              v-if="$can('add', 'WorkPlan', 'securityphotos')"
              class="md-success md-just-icon md-round"
              @click.stop.native="sendPhotos"
              :disabled="uploadDisabled"
            >
              <md-icon class="md-primary">cloud_upload</md-icon>
            </md-button>
            <md-button class="md-danger md-just-icon md-round" @click.stop.native="unloadPhotos">
              <md-icon class="md-primary">close</md-icon>
            </md-button>
          </div>
        </div>

        <simple-gallery
          v-if="!regularFile"
          :images="toUploadPhotos"
          :editor-enabled="true"
          @deletedImage="removePhoto"
          @updatedImages="toUploadPhotos = $event"
        />

        <template v-else>
          <matrix :images="toUploadPhotos" use-file-icons />
        </template>

        <slot name="upload-files-label" :to-upload-photos="toUploadPhotos">
          <div class="text-center">
            {{ toUploadPhotos.length }} archivo{{
            toUploadPhotos.length > 1 ? "s" : ""
            }}
            por subir.
          </div>
        </slot>
        <div v-if="buttonDisabled" class="text-center text-error">
          * Las imágenes señaladas no fueron tomadas este día o no son del formato
          JPG/JPEG.
        </div>
      </md-card-content>

      <md-card-content v-else>
        <slot name="empty-content">
          <loading-content v-if="loadingContentInfo" v-bind="loadingContentInfo" :loading="loading">
            <slot name="loadingContent"></slot>
          </loading-content>
        </slot>
      </md-card-content>
      <md-button
        v-if="showUploadButton && !toUploadPhotos.length && !loading"
        class="md-primary md-just-icon md-round float-right md-fileinput"
      >
        <md-icon>add</md-icon>
      </md-button>
      <input style="display: none;" ref="file" type="file" multiple @change="onFileChange" />
    </KDraggableField>
  </md-card>
</template>

<script>
import SimpleGallery from "@/components/Outputs/Images/SimpleGallery";
import Matrix from "@/components/Outputs/Images/Matrix";
import { validateImageData } from "@/utils";
import KDraggableField from "@/components/Inputs/KDraggableField";

const DEFAULT_EMPTY_STATE = {
  emptyLabel: "Subir fotos",
  emptyDescription: "Haga click o arrastre imágenes de su dispositivo",
  emptyIcon: "add_a_photo",
  loadingLabel: "Subiendo...",
};

export default {
  name: "PhotoConceptUploader",

  props: {
    todayImage: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      required: false,
      default: null,
    },
    concepts: {
      type: Array,
      required: true,
      validator(value) {
        return value.every((item) => item.id && item.name);
      },
    },
    conceptLabel: {
      type: String,
      default: "Tipo de foto",
    },
    showUploadButton: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingContentInfo: {
      type: Object,
      required: false,
      default: () => DEFAULT_EMPTY_STATE,
    },
    regularFile: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SimpleGallery,
    Matrix,
    KDraggableField,
  },

  computed: {
    uploadDisabled() {
      return this.buttonDisabled || !this.selectedConceptId;
    },
  },

  methods: {
    onFileChange(e) {
      if (
        !!this.toUploadPhotos.length ||
        this.loading ||
        !this.showUploadButton
      )
        return;
      const files = e && e.target ? e.target.files || e.dataTransfer.files : e;
      this.loadPhotos(files);
    },
    sendPhotos() {
      this.$emit("sendedPhotos", this.toUploadPhotos);
      // this.unloadPhotos()
    },

    unloadPhotos() {
      this.selectedConceptId = null;
      this.toUploadPhotos = [];
    },

    loadPhotos(files = []) {
      const toUploadPhotos = [];
      this.toUploadPhotos = toUploadPhotos;
      const { regularFile, todayImage } = this;
      const validateImage = !regularFile
        ? (file) => validateImageData(file, todayImage)
        : (file) =>
            Promise.resolve({
              file,
              src: file,
              error: false,
            });
      for (const file of files) {
        validateImage(file).then((image) => {
          toUploadPhotos.push(image);
        });
      }
    },

    removePhoto(image) {
      const images = this.toUploadPhotos;
      const index = images.indexOf(image);
      if (index === -1) {
        return;
      }
      images.splice(index, 1);
    },
  },

  data() {
    return {
      selectedConceptId: null,
      toUploadPhotos: [],
      buttonDisabled: true,
    };
  },

  watch: {
    toUploadPhotos(value) {
      this.buttonDisabled = false;
      if (this.todayImage) {
        value.forEach((element) => {
          if (element.error) {
            this.buttonDisabled = true;
          }
        });
      }
      this.$emit("loadedPhotos", value);
    },
    selectedConceptId(value) {
      this.$emit("updatedSelectedConcept", value);
    },
  },
};
</script>

<style lang="css" scoped>
.text-error {
  color: red;
}
.file-card {
  position: relative;
  color: green;
}
.float-right {
  position: absolute !important;
  bottom: 18px;
  right: 18px;
}
</style>

<style lang="css">
.md-select-menu {
  z-index: 110;
}
</style>



// WEBPACK FOOTER //
// src/components/Inputs/PhotoConceptUploader.vue